import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectSearch } from '../../mainFilter/searchSlice';
import { selectActiveDestination, selectDateFilter, selectFiltersLoaded, selectFilterData } from '../../mainFilter/selectors';
import { fetchPriceGraph, selectDestinationGraph, setPriceGraphNights } from '../priceGraphSlice';
import { selectSecondaryFilter } from '../../secondaryFilter/secondaryFilterSlice';
import { parseDate } from '../../../utilities/helpers';
import { differenceInCalendarDays, differenceInDays, parse } from 'date-fns';
import { useState } from 'react';
import { DATE_DDMMYYYY } from '../../../constants/constants';

export default function usePriceGraph () {
  const dispatch = useDispatch();
  const { nights, firstVisibleDate, destination: graphDestination } = useSelector(selectDestinationGraph);
  const filtersReady = useSelector(selectFiltersLoaded);
  const destination = useSelector(selectActiveDestination);
  const { when, who } = useSelector(selectSearch);
  const { searchEngine } = useSelector(selectFilterData);
  const secondaryFilter = useSelector(selectSecondaryFilter);
  const firstVisibleDateRef = useRef(firstVisibleDate);
  const { adults, children } = who;
  const currentDestination = graphDestination[destination.id];
  const dateFilter = useSelector(selectDateFilter);
  const nightsInit = dateFilter.minNights || (destination.min_nights < 4 ? 4 : destination.min_nights);
  const [nightsLimit, setNightsLimit] = useState({
    min: destination.min_nights || dateFilter.minNights || dateFilter.initial.flexible.min,
    max: destination.max_nights || dateFilter.maxNights || dateFilter.initial.flexible.max,
  });

  const getNightsToFetch = () => {
    const { type } = when;
    if (type === 'specific') {
      return differenceInCalendarDays(
        parse(dateFilter.endDate, DATE_DDMMYYYY, new Date()),
        parse(dateFilter.startDate, DATE_DDMMYYYY, new Date())
      )
    }

    if (type === 'month') {
      if (when.nights?.length > 0) {
        return Math.min(...when.nights);
      }

      return nightsInit;
    }

    if (type === 'flexible') {
      if (when.nights?.length > 0) {
        return Math.min(...when.nights);
      }

      return nightsInit;
    }
  }

  const filterNights = getNightsToFetch();

  // update graph if search params changed
  useEffect(() => {
    if (filtersReady) {
      dispatch(fetchPriceGraph(destination.id, nights))
    }

    setNightsLimit({
      min: destination.min_nights || dateFilter.minNights || dateFilter.initial.flexible.min,
      max: destination.max_nights || dateFilter.maxNights || dateFilter.initial.flexible.max,
    });
  }, [dispatch, filtersReady, nights, destination.id, adults, children, secondaryFilter.data, searchEngine.fromwhere])

  // lazy load for price graph
  useEffect(() => {
    if (currentDestination) {
      const firstVisibleDateParsed = parseDate(firstVisibleDate);
      const firstVisibleDateRefParsed = parseDate(firstVisibleDateRef.current);

      if (firstVisibleDateRefParsed < firstVisibleDateParsed) {
        // Move forward
        if (differenceInCalendarDays(parseDate(currentDestination.dates[currentDestination.dates.length - 1].start), firstVisibleDateParsed) < 30) {
          dispatch(fetchPriceGraph(destination.id, filterNights || nightsLimit.min || 4, 1))
        }
      }
      if (firstVisibleDateRefParsed > firstVisibleDateParsed) {
        // Move back
        if (differenceInCalendarDays(firstVisibleDateParsed, parseDate(currentDestination.dates[0].start)) < 30) {
          dispatch(fetchPriceGraph(destination.id, filterNights || nightsLimit.min || 4, -1))
        }
      }

      firstVisibleDateRef.current = firstVisibleDate;
    }
  }, [dispatch, currentDestination, firstVisibleDate])


  useEffect(() => {
    if (when?.nights?.length > 0) {
      dispatch(setPriceGraphNights(Math.min(...when.nights)))
    } else {
      dispatch(setPriceGraphNights(dateFilter.minNights || (destination.min_nights < 4 ? 4 : destination.min_nights)))
    }
  }, [dispatch, dateFilter.minNights, destination.min_nights, when?.nights])


  // TODO: clear price graph nights if new nights was set from filter.
  useEffect(() => {
    const { type, startDate, endDate } = when;
    if (
      type === 'specific' &&
      startDate &&
      endDate
    ) {
      const startDateParsed = parse(startDate, DATE_DDMMYYYY, new Date());
      const endDateParsed = parse(endDate, DATE_DDMMYYYY, new Date());

      dispatch(setPriceGraphNights(differenceInDays(endDateParsed, startDateParsed)));
    }
  }, [dispatch, when])

  useEffect(() => {
    const { nights } = when;
    if (nights?.length > 0) {
      dispatch(setPriceGraphNights(Math.min(...nights)));
    }
  }, [dispatch, when]);

  return { nightsLimit }
}
